import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Form, Input, Select, InputNumber, DatePicker, TimePicker, Button, message, Row, Col, Card, List, Typography, Tooltip } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsersByOrganisation, clearOrganisationUsers } from '../../redux/userOrganisationsSlice';
import dayjs from 'dayjs';
import ConfirmModal from '../ConfirmModal';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

const workSchedules = [
    '6/1', '5/2', '4/4', '4/3', '4/2', '3/3', '2/2', '1/3', '1/2',
    'По выходным', 'Свободный', 'Другое'
];

const vacancyReasons = [
    'Новая вакансия',
    'Внутренний перевод',
    'Декрет',
    'Замена'
];

const CreateFromTemplateModal = ({
    visible,
    onCancel,
    onSubmit,
    requestsTemplate,
    onCommentSubmit,
    organisations,
    users,
}) => {
    const dispatch = useDispatch();
    const { organisationUsers: orgUsers, isLoading: usersLoading } = useSelector(state => state.userOrganisations);
    const { orgId } = useSelector(state => state.auth);
    const [form] = Form.useForm();
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [filteredDepartments, setFilteredDepartments] = useState([]);
    const [selectedOrganisation, setSelectedOrganisation] = useState(null);

    const initialValuesRef = useRef({});

    // Состояния для кнопок-чекбоксов
    const [businessTripsRequired, setBusinessTripsRequired] = useState(false);
    const [relocationWillingness, setRelocationWillingness] = useState(false);
    const [communicationCompensation, setCommunicationCompensation] = useState(false);
    const [carCompensation, setCarCompensation] = useState(false);
    const [isMaterialResponsible, setIsMaterialResponsible] = useState(false);
    const [driverLicenseRequired, setDriverLicenseRequired] = useState(false);
    const [ownCarRequired, setOwnCarRequired] = useState(false);
    const [loading, setLoading] = useState(false);

    const filterDepartments = useCallback((companyId) => {
        const numericCompanyId = Number(companyId);
        const org = organisations.find(o => o.organisation_id === numericCompanyId);
        setFilteredDepartments(org?.departments || []);
    }, [organisations]);

    const getValidationRules = useCallback((fieldName, isDraft = false) => {
        const draftRequiredFields = {
            organisation: { required: true, message: 'Выберите компанию' },
            job_title: { required: true, message: 'Укажите название должности' }
        };

        if (isDraft) {
            return draftRequiredFields[fieldName] ? [draftRequiredFields[fieldName]] : [];
        }

        const requiredFields = {
            organisation: { required: true, message: 'Выберите компанию' },
            submission_date: { required: true, message: 'Укажите дату подачи заявки' },
            department: { required: true, message: 'Выберите отдел' },
            city: { required: true, message: 'Укажите город' },
            planned_start_date: { required: true, message: 'Укажите планируемую дату выхода сотрудника на работу' },
            author_name: { required: true, message: 'Укажите ФИО автора заявки' },
            author_position: { required: true, message: 'Укажите должность автора заявки' },
            job_title: { required: true, message: 'Укажите название должности' },
            count: { required: true, message: 'Укажите количество позиций' },
            rate: { required: true, message: 'Выберите ставку' },
            vacancy_reason: { required: true, message: 'Выберите причину возникновения вакансии' },
            supervisor: { required: true, message: 'Укажите начальника' },
            subordinates_count: { required: true, message: 'Укажите количество подчиненных' },
            is_material_responsible: { required: true, message: 'Укажите материально ответственное лицо' },
            tasks: { required: true, message: 'Опишите задачи' },
            expected_results: { required: true, message: 'Опишите ожидаемые результаты' },
            responsibilities: { required: true, message: 'Опишите функциональные обязанности' },
            work_schedule: { required: true, message: 'Выберите график работы' },
            work_start_time: { required: true, message: 'Укажите время начала работы' },
            work_end_time: { required: true, message: 'Укажите время окончания работы' },
            salary_structure: { required: true, message: 'Опишите структуру заработной платы' },
            probation_salary_min: { required: true, message: 'Укажите минимальную зарплату на период испытательного срока' },
            probation_salary_max: { required: true, message: 'Укажите максимальную зарплату на период испытательного срока' },
            regular_salary_min: { required: true, message: 'Укажите минимальную зарплату после испытательного срока' },
            regular_salary_max: { required: true, message: 'Укажите максимальную зарплату после испытательного срока' },
            work_address: { required: true, message: 'Укажите адрес рабочего места' }
        };

        return requiredFields[fieldName] ? [requiredFields[fieldName]] : [];
    }, []);

    const updateFormValidation = useCallback((isDraft) => {
        if (isDraft) {
            // Для черновика только минимальные требования
            const draftFields = ['organisation', 'job_title'];
            draftFields.forEach(fieldName => {
                const rules = getValidationRules(fieldName, isDraft);
                form.setFields([{ name: fieldName, rules }]);
            });
        } else {
            // Для создания заявки - все обязательные поля
            const fields = [
                'organisation', 'submission_date', 'department', 'city',
                'planned_start_date', 'author_name', 'author_position', 'job_title',
                'count', 'rate', 'vacancy_reason', 'supervisor', 'subordinates_count',
                'is_material_responsible', 'tasks', 'expected_results', 'responsibilities',
                'work_schedule', 'work_start_time', 'work_end_time',
                'salary_structure', 'probation_salary_min',
                'probation_salary_max', 'regular_salary_min', 'regular_salary_max', 'work_address'
            ];

            fields.forEach(fieldName => {
                const rules = getValidationRules(fieldName, isDraft);
                form.setFields([{ name: fieldName, rules }]);
            });
        }
    }, [form, getValidationRules]);

    useEffect(() => {
        if (!visible) {
            form.resetFields();
            setSelectedTemplate(null);
            setShowForm(false);
            setSelectedOrganisation(null);
            setBusinessTripsRequired(false);
            setRelocationWillingness(false);
            setCommunicationCompensation(false);
            setCarCompensation(false);
            setIsMaterialResponsible(false);
            setDriverLicenseRequired(false);
            setOwnCarRequired(false);
            dispatch(clearOrganisationUsers());
        } else {
            // Автоматически заполняем ФИО автора из localStorage при открытии модального окна
            const firstName = localStorage.getItem('first_name') || '';
            const lastName = localStorage.getItem('last_name') || '';
            const authorName = `${firstName} ${lastName}`.trim();

            if (authorName) {
                form.setFieldsValue({
                    author_name: authorName
                });
            }
        }
    }, [visible, dispatch, form]);

    useEffect(() => {
        if (organisations?.length > 0) {
            const organisationId = form.getFieldValue('organisation_id');
            if (organisationId) {
                filterDepartments(organisationId);
            }
        }
    }, [organisations, form, filterDepartments]);

    // Добавляем useEffect для обработки случая, когда форма уже содержит организацию
    useEffect(() => {
        if (showForm && selectedTemplate) {
            const organisationId = form.getFieldValue('organisation');
            if (organisationId && !selectedOrganisation) {
                setSelectedOrganisation(organisationId);
                filterDepartments(organisationId);
                dispatch(fetchUsersByOrganisation(organisationId));
            }
        }
    }, [showForm, selectedTemplate, form, selectedOrganisation, dispatch, filterDepartments]);

    const handleCompanyChange = (orgId) => {
        setSelectedOrganisation(orgId);
        form.setFieldsValue({ department: undefined });
        filterDepartments(orgId);
        dispatch(clearOrganisationUsers());

        // Загружаем пользователей организации
        if (orgId) {
            dispatch(fetchUsersByOrganisation(orgId));
        }
    };

    const handleTemplateSelect = (template) => {
        setSelectedTemplate(template);
        setShowForm(true);

        // Устанавливаем состояния кнопок-чекбоксов
        setBusinessTripsRequired(template.requires_travel || false);
        setRelocationWillingness(template.requires_relocation || false);
        setCommunicationCompensation(template.compensate_phone || false);
        setCarCompensation(template.compensate_car || false);
        setIsMaterialResponsible(template.is_material_responsible || false);
        setDriverLicenseRequired(template.requires_license || false);
        setOwnCarRequired(template.requires_car || false);

        if (template.organisation) {
            setSelectedOrganisation(template.organisation);
            form.setFieldsValue({ department: undefined });
            filterDepartments(template.organisation);
            dispatch(clearOrganisationUsers());
            dispatch(fetchUsersByOrganisation(template.organisation));
        }

        const formData = {
            organisation: template.organisation || null,
            approver_management: template.approver_management || [],
            approver_hr: template.approver_hr || null,

            submission_date: template.submission_date ? dayjs(template.submission_date) : dayjs(),
            department: template.department || null,
            city: template.city || '',
            planned_start_date: template.planned_start_date || '',
            author_name: template.author_name || '',

            job_title: template.job_title || '',
            count: template.positions_count || 1,
            rate: template.rate || '1',
            vacancy_reason: template.vacancy_reason || null,
            supervisor: template.supervisors || '',
            subordinates_count: template.subordinates_count || 0,
            tasks: template.tasks || '',
            responsibilities: template.functional_responsibilities || '',
            reporting: template.reporting_details || '',
            expected_results: template.expected_results || '',
            work_schedule: template.work_schedule || null,
            work_start_time: template.work_start_time ? dayjs(template.work_start_time, 'HH:mm') : undefined,
            work_end_time: template.work_end_time ? dayjs(template.work_end_time, 'HH:mm') : undefined,
            work_location_ratio: template.field_vs_office || '',
            business_trips_details: template.travel_details || '',
            salary_structure: template.salary_structure || '',
            probation_salary_min: template.trial_salary ? template.trial_salary.split(' - ')[0] : undefined,
            probation_salary_max: template.trial_salary ? template.trial_salary.split(' - ')[1] : undefined,
            regular_salary_min: template.post_trial_salary ? template.post_trial_salary.split(' - ')[0] : undefined,
            regular_salary_max: template.post_trial_salary ? template.post_trial_salary.split(' - ')[1] : undefined,
            work_address: template.workplace_address || '',
            gender: template.gender || null,
            age: template.age || '',
            marital_status: template.marital_status || [],
            driver_license_categories: template.license_categories || [],
            education: template.education_levels || [],
            computer_skills: template.computer_skills_level || null,
            skills_1c: template.skills_1c || '',
            software_knowledge: template.software_skills || [],
            language_knowledge: template.language_skills || [],
            work_experience: template.experience_description || '',
            personal_qualities: template.personal_traits || '',
            professional_skills: template.professional_skills || '',
            preferred_companies: template.preferred_companies || '',
            undesired_companies: template.unwanted_companies || '',
            other_conditions: template.other_conditions || '',
        };

        setTimeout(() => {
            // Автоматически заполняем ФИО автора из localStorage
            const firstName = localStorage.getItem('first_name') || '';
            const lastName = localStorage.getItem('last_name') || '';
            const authorName = `${firstName} ${lastName}`.trim();

            if (authorName) {
                formData.author_name = authorName;
            }

            form.setFieldsValue(formData);
            // Сохраняем начальные значения после заполнения формы
            setTimeout(() => {
                initialValuesRef.current = form.getFieldsValue(true);
                setHasUnsavedChanges(false);
            }, 0);

            // Инициализируем валидацию формы
            updateFormValidation(false);
        }, 100);
    };

    // Функция для проверки наличия изменений
    const checkForChanges = useCallback(() => {
        const currentValues = form.getFieldsValue(true);
        return JSON.stringify(currentValues) !== JSON.stringify(initialValuesRef.current);
    }, [form]);

    // Обработчик изменения формы
    const handleFormChange = useCallback(() => {
        setHasUnsavedChanges(checkForChanges());
    }, [checkForChanges]);

    // Обработчик закрытия модального окна
    const handleModalCancel = useCallback(() => {
        if (hasUnsavedChanges) {
            setShowConfirm(true);
        } else {
            onCancel();
        }
    }, [hasUnsavedChanges, onCancel]);

    // Подтверждение закрытия
    const handleConfirmClose = useCallback((shouldClose) => {
        if (shouldClose) {
            onCancel();
        }
        setShowConfirm(false);
    }, [onCancel]);

    const handleFinish = async (isTemplate = false) => {
        try {
            setLoading(true);
            updateFormValidation(isTemplate);

            let values;

            if (isTemplate) {
                // Для черновика проверяем только минимальные поля
                const draftRequiredFields = ['organisation', 'job_title'];
                await form.validateFields(draftRequiredFields);
                values = form.getFieldsValue();
            } else {
                // Для создания заявки проверяем все обязательные поля
                values = await form.validateFields();
            }

            const data = {
                submission_date: values.submission_date ? dayjs(values.submission_date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
                organisation: values.organisation,
                department: values.department,
                city: values.city,
                planned_start_date: values.planned_start_date,
                author: values.author,
                author_position: values.author_position,
                assignee_id: values.assignee_id,
                is_template: isTemplate,
                status: 'new',
                job_title: values.job_title,
                positions_count: values.count || 1,
                rate: values.rate ? String(values.rate) : '0.25',
                vacancy_reason: values.vacancy_reason,
                supervisors: values.supervisor,
                subordinates_count: values.subordinates_count ? String(values.subordinates_count) : '0',
                is_material_responsible: isMaterialResponsible,
                tasks: values.tasks,
                expected_results: values.expected_results,
                functional_responsibilities: values.responsibilities,
                reporting_details: values.reporting,
                work_schedule: values.work_schedule,
                work_start_time: values.work_start_time ? values.work_start_time.format('HH:mm') : '',
                work_end_time: values.work_end_time ? values.work_end_time.format('HH:mm') : '',
                field_vs_office: values.work_location_ratio,
                requires_travel: businessTripsRequired,
                travel_details: values.business_trips_details,
                requires_relocation: relocationWillingness,
                salary_structure: values.salary_structure,
                trial_salary: values.probation_salary_min !== undefined && values.probation_salary_max !== undefined
                    ? `${values.probation_salary_min} - ${values.probation_salary_max}`
                    : '',
                post_trial_salary: values.regular_salary_min !== undefined && values.regular_salary_max !== undefined
                    ? `${values.regular_salary_min} - ${values.regular_salary_max}`
                    : '',
                compensate_phone: communicationCompensation,
                compensate_car: carCompensation,
                workplace_address: values.work_address,
                gender: values.gender,
                age: values.age,
                marital_status: values.marital_status || [],
                requires_license: driverLicenseRequired,
                license_categories: values.driver_license_categories || [],
                requires_car: ownCarRequired,
                education_levels: values.education || [],
                computer_skills_level: values.computer_skills,
                skills_1c: values.skills_1c,
                software_skills: (values.software_knowledge || [])
                    .filter(sk => sk && sk.program && sk.program.trim() !== '')
                    .map(sk => ({
                        program: sk.program.trim(),
                        level: sk.level || 'Не требуется'
                    })),
                language_skills: (values.language_knowledge || [])
                    .filter(lang => lang && lang.language && lang.language.trim() !== '')
                    .map(lang => ({
                        language: lang.language.trim(),
                        level: lang.level || 'Не требуется'
                    })),
                experience_description: values.work_experience,
                personal_traits: values.personal_qualities,
                professional_skills: values.professional_skills,
                preferred_companies: values.preferred_companies,
                unwanted_companies: values.undesired_companies,
                other_conditions: values.other_conditions,
                comment: values.comment,
            };

            onSubmit(selectedTemplate.id, orgId, data);
            form.resetFields();
            onCommentSubmit(selectedTemplate.id, isTemplate ? "Сохранение черновика" : "Создание из черновика");
            setSelectedTemplate(null);
            setShowForm(false);
            message.success(isTemplate ? 'Черновик сохранен' : 'Заявка создана');
        } catch (error) {
            console.error('Ошибка валидации формы:', error);
            if (error?.errorFields?.length > 0) {
                form.scrollToField(error.errorFields[0].name, {
                    behavior: 'smooth',
                    block: 'center',
                });
                message.error('Пожалуйста, заполните все обязательные поля');
            }
        } finally {
            setLoading(false);
        }
    };
    const handleBack = () => {
        setShowForm(false);
        setSelectedTemplate(null);
        form.resetFields();
    };

    const renderTemplateList = () => (
        <div>
            <Title level={4}>Выберите черновик заявки</Title>
            <List
                dataSource={requestsTemplate}
                renderItem={(template) => (
                    <List.Item>
                        <Card
                            hoverable
                            style={{ width: '100%', cursor: 'pointer' }}
                            onClick={() => handleTemplateSelect(template)}
                        >
                            <div>
                                <Title level={5}>{template.job_title || `Черновик #${template.id}`}</Title>
                                <Text type="secondary">
                                    {template.positions_count ? `${template.positions_count} позиций` : ''}
                                    {template.organisation && organisations.find(org => org.organisation_id === template.organisation)?.name &&
                                        ` • ${organisations.find(org => org.organisation_id === template.organisation).name}`}
                                </Text>
                            </div>
                        </Card>
                    </List.Item>
                )}
            />
        </div>
    );

    const renderForm = () => (
        <div>
            <div style={{ marginBottom: 16 }}>
                <Tooltip title="Вернуться к выбору черновика">
                    <Button onClick={handleBack} style={{ marginBottom: 16 }}>
                        ← Назад к списку черновиков
                    </Button>
                </Tooltip>
                {selectedTemplate && (
                    <div style={{ marginBottom: 16 }}>
                        <Text strong>Выбранный черновик: {selectedTemplate.job_title || `Черновик #${selectedTemplate.id}`}</Text>
                    </div>
                )}
            </div>
            <Form
                form={form}
                layout="vertical"
                scrollToFirstError={{
                    behavior: 'smooth',
                    block: 'center'
                }}
                onFieldsChange={handleFormChange}
            >
                {/* Согласование */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Согласование
                    </h3>
                    <Form.Item name="organisation" label="Компания" rules={getValidationRules('organisation', false)}>
                        <Select showSearch optionFilterProp="children" placeholder="Выберите компанию" onChange={handleCompanyChange}>
                            {organisations.map(o => (
                                <Option key={o.id} value={o.organisation_id}>{o.name}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.List name="approver_management">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Row gutter={8} key={key}>
                                        <Col span={22}>
                                            <Form.Item
                                                {...restField}
                                                name={name}
                                                label="Согласующий"
                                            >
                                                <Select placeholder="Выберите согласующего" loading={usersLoading}>
                                                    {(selectedOrganisation && orgUsers.length > 0 ? orgUsers : users).map(u => (
                                                        <Option key={u.id} value={u.id}>{u.last_name} {u.first_name}</Option>
                                                    ))}
                                                </Select>
                                            </Form.Item>
                                        </Col>
                                        <Col span={2} style={{ marginTop: '6px', display: 'flex', alignItems: 'center' }}>
                                            <Button danger onClick={() => remove(name)}>Удалить</Button>
                                        </Col>
                                    </Row>
                                ))}
                                <Form.Item>
                                    <Button type="dashed" onClick={() => add()}>Добавить согласующего</Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>

                    {/* <Form.Item
                        name="approver_hr"
                        label="Согласующий HR"
                        rules={[{ required: true, message: 'Выберите согласующего HR' }]}
                    >
                        <Select placeholder="Выберите согласующего" loading={usersLoading}>
                            {(selectedOrganisation && orgUsers.length > 0 ? orgUsers : users).map(u => (
                                <Option key={u.id} value={u.id}>{u.last_name} {u.first_name}</Option>
                            ))}
                        </Select>
                    </Form.Item> */}
                </div>

                {/* Основная информация */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Основная информация
                    </h3>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="job_title" label="Название должности" rules={getValidationRules('job_title', false)}>
                                <Input />
                            </Form.Item>

                            <Form.Item name="department" label="Отдел" rules={getValidationRules('department', false)}>
                                <Select placeholder="Выберите отдел">
                                    {filteredDepartments.map(d => (
                                        <Option key={d.id} value={d.id}>{d.name}</Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item name="city" label="Город" rules={getValidationRules('city', false)}>
                                <Input />
                            </Form.Item>

                            <Form.Item name="submission_date" label="Дата подачи заявки" rules={getValidationRules('submission_date', false)}>
                                <DatePicker style={{ width: '100%' }} defaultValue={dayjs()} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="author_name" label="Автор заявки, ФИО" rules={getValidationRules('author_name', false)}>
                                <Input />
                            </Form.Item>

                            <Form.Item name="author_position" label="Автор заявки, должность" rules={getValidationRules('author_position', false)}>
                                <Input />
                            </Form.Item>

                            <Form.Item name="planned_start_date" label="Планируемая дата выхода сотрудника на работу" rules={getValidationRules('planned_start_date', false)}>
                                <Input placeholder="Можно указать дату, месяц или период (например: 01.09.2023, сентябрь 2023, 3-й квартал 2023)" />
                            </Form.Item>

                            <Form.Item name="count" label="Количество позиций" rules={getValidationRules('count', false)}>
                                <InputNumber min={1} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                {/* Информация о вакансии */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Информация о вакансии
                    </h3>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="rate"
                                label="Ставка"
                                rules={getValidationRules('rate', false)}
                            >
                                <Select placeholder="Выберите ставку">
                                    {['0.25', '0.5', '0.75', '1', '1.25', '1.5', '1.75', '2'].map(value => (
                                        <Option key={value} value={value}>
                                            {value}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item name="supervisor" label="Начальник" rules={getValidationRules('supervisor', false)}>
                                <Input />
                            </Form.Item>

                            <Form.Item name="is_material_responsible" label="Материально ответственное лицо">
                                <Button
                                    type={isMaterialResponsible ? "primary" : "default"}
                                    onClick={() => setIsMaterialResponsible(!isMaterialResponsible)}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: isMaterialResponsible ? 'bold' : 'normal'
                                    }}
                                >
                                    {isMaterialResponsible ? '✓ Материально ответственное лицо' : 'Материально ответственное лицо'}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="vacancy_reason" label="Причина возникновения вакансии" rules={getValidationRules('vacancy_reason', false)}>
                                <Select>
                                    {vacancyReasons.map(reason => (
                                        <Option key={reason} value={reason}>{reason}</Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item name="subordinates_count" label="Подчиненные (количество)" rules={getValidationRules('subordinates_count', false)}>
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                {/* Должностные обязанности */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Должностные обязанности
                    </h3>
                    <Form.Item name="tasks" label="Задачи" rules={getValidationRules('tasks', false)}>
                        <TextArea rows={3} />
                    </Form.Item>

                    <Form.Item name="responsibilities" label="Функциональные обязанности" rules={getValidationRules('responsibilities', false)}>
                        <TextArea rows={3} />
                    </Form.Item>

                    <Form.Item name="reporting" label="Отчетность о проделанной работе">
                        <TextArea rows={3} />
                    </Form.Item>

                    <Form.Item name="expected_results" label="Ожидаемые результаты" rules={getValidationRules('expected_results', false)}>
                        <TextArea rows={3} />
                    </Form.Item>
                </div>

                {/* Условия работы */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Условия работы
                    </h3>


                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="work_schedule" label="График работы" rules={getValidationRules('work_schedule', false)}>
                                <Select placeholder="Выберите график работы">
                                    {workSchedules.map(schedule => (
                                        <Option key={schedule} value={schedule}>{schedule}</Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item name="work_start_time" label="Начало работы" rules={getValidationRules('work_start_time', false)}>
                                <TimePicker
                                    style={{ width: '100%' }}
                                    format="HH:mm"
                                    needConfirm={false}
                                    placeholder="Выберите время начала работы"
                                />
                            </Form.Item>

                            <Form.Item name="business_trips_required" label="Командировки">
                                <Button
                                    type={businessTripsRequired ? "primary" : "default"}
                                    onClick={() => setBusinessTripsRequired(!businessTripsRequired)}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: businessTripsRequired ? 'bold' : 'normal'
                                    }}
                                >
                                    {businessTripsRequired ? '✓ Командировки' : 'Командировки'}
                                </Button>
                            </Form.Item>

                            {businessTripsRequired && (
                                <Form.Item name="business_trips_details" label="Частота и длительность командировок">
                                    <Input placeholder="Например: 2 раза в месяц, 3-5 дней" />
                                </Form.Item>
                            )}
                        </Col>
                        <Col span={12}>
                            <Form.Item name="work_location_ratio" label="Соотношение работы: дом/офис">
                                <Input placeholder="Например: 70% офис, 30% дом" />
                            </Form.Item>

                            <Form.Item name="work_end_time" label="Окончание работы" rules={getValidationRules('work_end_time', false)}>
                                <TimePicker
                                    style={{ width: '100%' }}
                                    format="HH:mm"
                                    needConfirm={false}
                                    placeholder="Выберите время окончания работы"
                                />
                            </Form.Item>

                            <Form.Item name="relocation_willingness" label="Готовность к переезду">
                                <Button
                                    type={relocationWillingness ? "primary" : "default"}
                                    onClick={() => setRelocationWillingness(!relocationWillingness)}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: relocationWillingness ? 'bold' : 'normal'
                                    }}
                                >
                                    {relocationWillingness ? '✓ Готовность к переезду' : 'Готовность к переезду'}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                {/* Зарплата */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Зарплата
                    </h3>
                    <Form.Item name="salary_structure" label="Заработная плата (структура)" rules={getValidationRules('salary_structure', false)}>
                        <TextArea rows={2} placeholder="Опишите как формируется заработная плата" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Зарплата на период испытательного срока (BYN)"
                                required
                                validateStatus={form.getFieldError('probation_salary_min')?.length > 0 || form.getFieldError('probation_salary_max')?.length > 0 ? 'error' : ''}
                                help={form.getFieldError('probation_salary_min')?.[0] || form.getFieldError('probation_salary_max')?.[0]}
                            >
                                <Input.Group compact>
                                    <Form.Item
                                        name="probation_salary_min"
                                        noStyle
                                        rules={getValidationRules('probation_salary_min', false)}
                                    >
                                        <InputNumber
                                            min={0}
                                            placeholder="От"
                                            style={{ width: '50%' }}
                                            parser={value => value.replace(/\s?BYN/g, '')}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        name="probation_salary_max"
                                        noStyle
                                        rules={getValidationRules('probation_salary_max', false)}
                                    >
                                        <InputNumber
                                            min={0}
                                            placeholder="До"
                                            style={{ width: '50%' }}
                                            parser={value => value.replace(/\s?BYN/g, '')}
                                        />
                                    </Form.Item>
                                </Input.Group>
                            </Form.Item>

                            <Form.Item name="communication_compensation" label="Компенсация затрат: связь">
                                <Button
                                    type={communicationCompensation ? "primary" : "default"}
                                    onClick={() => setCommunicationCompensation(!communicationCompensation)}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: communicationCompensation ? 'bold' : 'normal'
                                    }}
                                >
                                    {communicationCompensation ? '✓ Компенсация затрат: связь' : 'Компенсация затрат: связь'}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Зарплата после испытательного срока (BYN)"
                                required
                                validateStatus={form.getFieldError('regular_salary_min')?.length > 0 || form.getFieldError('regular_salary_max')?.length > 0 ? 'error' : ''}
                                help={form.getFieldError('regular_salary_min')?.[0] || form.getFieldError('regular_salary_max')?.[0]}
                            >
                                <Input.Group compact>
                                    <Form.Item
                                        name="regular_salary_min"
                                        noStyle
                                        rules={getValidationRules('regular_salary_min', false)}
                                    >
                                        <InputNumber
                                            min={0}
                                            placeholder="От"
                                            style={{ width: '50%' }}
                                            parser={value => value.replace(/\s?BYN/g, '')}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        name="regular_salary_max"
                                        noStyle
                                        rules={getValidationRules('regular_salary_max', false)}
                                    >
                                        <InputNumber
                                            min={0}
                                            placeholder="До"
                                            style={{ width: '50%' }}
                                            parser={value => value.replace(/\s?BYN/g, '')}
                                        />
                                    </Form.Item>
                                </Input.Group>
                            </Form.Item>

                            <Form.Item name="car_compensation" label="Компенсация затрат: личный автомобиль">
                                <Button
                                    type={carCompensation ? "primary" : "default"}
                                    onClick={() => setCarCompensation(!carCompensation)}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: carCompensation ? 'bold' : 'normal'
                                    }}
                                >
                                    {carCompensation ? '✓ Компенсация затрат: личный автомобиль' : 'Компенсация затрат: личный автомобиль'}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item name="work_address" label="Адрес рабочего места" rules={getValidationRules('work_address', false)}>
                        <Input placeholder="Полный адрес рабочего места" />
                    </Form.Item>
                </div>

                {/* Требования к кандидату */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Требования к кандидату
                    </h3>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="gender" label="Пол">
                                <Select options={[
                                    { value: 'Мужской', label: 'Мужской' },
                                    { value: 'Женский', label: 'Женский' },
                                    { value: 'Не имеет значения', label: 'Не имеет значения' },
                                ]} />
                            </Form.Item>

                            <Form.Item name="marital_status" label="Семейное положение">
                                <Select
                                    mode="multiple"
                                    options={[
                                        { value: 'Холост/Не замужем', label: 'Холост/Не замужем' },
                                        { value: 'Женат/Замужем', label: 'Женат/Замужем' },
                                        { value: 'Разведен(а)', label: 'Разведен(а)' },
                                        { value: 'Вдовец/Вдова', label: 'Вдовец/Вдова' },
                                        { value: 'Не имеет значения', label: 'Не имеет значения' },
                                    ]}
                                />
                            </Form.Item>

                            <Form.Item name="driver_license_required" label="Наличие водительского удостоверения">
                                <Button
                                    type={driverLicenseRequired ? "primary" : "default"}
                                    onClick={() => setDriverLicenseRequired(!driverLicenseRequired)}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: driverLicenseRequired ? 'bold' : 'normal'
                                    }}
                                >
                                    {driverLicenseRequired ? '✓ Наличие водительского удостоверения' : 'Наличие водительского удостоверения'}
                                </Button>
                            </Form.Item>

                            {driverLicenseRequired && (
                                <Form.Item name="driver_license_categories" label="Категории водительских прав">
                                    <Select
                                        mode="multiple"
                                        options={[
                                            'АМ', 'А', 'А1', 'В', 'С', 'D', 'ВЕ', 'СЕ', 'DE', 'F', 'I'
                                        ].map(cat => ({ value: cat, label: cat }))}
                                    />
                                </Form.Item>
                            )}
                        </Col>
                        <Col span={12}>
                            <Form.Item name="age" label="Возраст">
                                <Input placeholder="Укажите возраст или диапазон" />
                            </Form.Item>

                            <Form.Item name="education" label="Образование">
                                <Select
                                    mode="multiple"
                                    options={[
                                        'Базовое',
                                        'Среднее',
                                        'Профессионально-техническое',
                                        'Среднее специальное',
                                        'Высшее'
                                    ].map(ed => ({ value: ed, label: ed }))}
                                />
                            </Form.Item>

                            <Form.Item name="own_car_required" label="Наличие личного автомобиля">
                                <Button
                                    type={ownCarRequired ? "primary" : "default"}
                                    onClick={() => setOwnCarRequired(!ownCarRequired)}
                                    style={{
                                        width: '100%',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: ownCarRequired ? 'bold' : 'normal'
                                    }}
                                >
                                    {ownCarRequired ? '✓ Наличие личного автомобиля' : 'Наличие личного автомобиля'}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </div>

                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Навыки и опыт
                    </h3>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="computer_skills" label="Уровень владения ПК (Word, Excel и др.)">
                                <Select
                                    options={[
                                        'Не требуется',
                                        'Базовый',
                                        'Уверенный',
                                        'Продвинутый'
                                    ].map(level => ({ value: level, label: level }))}
                                />
                            </Form.Item>
                        </Col>

                        <Col span={12}>
                            <Form.Item name="skills_1c" label="Навыки в работе с 1С">
                                <Input.TextArea rows={1} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.List name="software_knowledge">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Row gutter={8} key={key}>
                                        <Col span={12}>
                                            <Form.Item {...restField} name={[name, 'program']} label="Программа">
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col span={10}>
                                            <Form.Item {...restField} name={[name, 'level']} label="Уровень владения">
                                                <Select options={[
                                                    'Базовый',
                                                    'Уверенный',
                                                    'Продвинутый'
                                                ].map(l => ({ value: l, label: l }))} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={2} style={{ marginTop: '6px', display: 'flex', alignItems: 'center' }}>
                                            <Button danger onClick={() => remove(name)}>Удалить</Button>
                                        </Col>
                                    </Row>
                                ))}
                                <Form.Item label="Знание других специализированных программ">
                                    <Button type="dashed" onClick={() => add()}>Добавить программу</Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>

                    <Form.List name="language_knowledge">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map(({ key, name, ...restField }) => (
                                    <Row gutter={8} key={key}>
                                        <Col span={12}>
                                            <Form.Item {...restField} name={[name, 'language']} label="Язык">
                                                <Input />
                                            </Form.Item>
                                        </Col>
                                        <Col span={10}>
                                            <Form.Item {...restField} name={[name, 'level']} label="Уровень владения">
                                                <Select options={[
                                                    'Базовый',
                                                    'Уверенный',
                                                    'Продвинутый'
                                                ].map(l => ({ value: l, label: l }))} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={2} style={{ marginTop: '6px', display: 'flex', alignItems: 'center' }}>
                                            <Button danger onClick={() => remove(name)}>Удалить</Button>
                                        </Col>
                                    </Row>
                                ))}
                                <Form.Item label="Знание иностранных языков">
                                    <Button type="dashed" onClick={() => add()}>Добавить язык</Button>
                                </Form.Item>
                            </>
                        )}
                    </Form.List>

                    <Form.Item name="work_experience" label="Опыт работы (область, продолжительность)">
                        <TextArea rows={2} />
                    </Form.Item>
                </div>

                {/* Личные и профессиональные качества */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Личные и профессиональные качества
                    </h3>

                    <Form.Item name="personal_qualities" label="Личные качества кандидатов">
                        <TextArea rows={3} />
                    </Form.Item>

                    <Form.Item name="professional_skills" label="Профессиональные навыки кандидатов">
                        <TextArea rows={3} />
                    </Form.Item>
                </div>

                {/* Дополнительные требования */}
                <div style={{ marginBottom: 24, padding: '16px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                    <h3 style={{ marginBottom: 16, color: '#1890ff', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
                        Дополнительные требования
                    </h3>
                    <Form.Item name="preferred_companies" label="Компании, из которых предпочтительно рассматривать кандидатов">
                        <TextArea rows={2} />
                    </Form.Item>

                    <Form.Item name="undesired_companies" label="Компании, из которых нежелательно рассматривать кандидатов">
                        <TextArea rows={2} />
                    </Form.Item>

                    <Form.Item name="other_conditions" label="Другие условия">
                        <TextArea rows={3} />
                    </Form.Item>
                </div>

            </Form >
        </div >
    );

    const footerButtons = [
        <Tooltip key="back" title="Закрыть модальное окно без сохранения изменений">
            <Button onClick={onCancel}>
                Закрыть
            </Button>
        </Tooltip>,
        <Tooltip key="draft" title="Сохранить как черновик для последующего редактирования">
            <Button
                type="default"
                loading={loading}
                onClick={() => handleFinish(true)}
            >
                Сохранить как черновик
            </Button>
        </Tooltip>,
        <Tooltip key="submit" title="Создать заявку и отправить на согласование">
            <Button
                type="primary"
                loading={loading}
                onClick={() => handleFinish(false)}
            >
                Создать заявку
            </Button>
        </Tooltip>
    ];

    return (
        <>
            <Modal
                visible={visible}
                title="Создание заявки из черновика"
                onCancel={handleModalCancel}
                footer={showForm ? footerButtons : null}
                destroyOnClose
                width={1000}
            >
                {!showForm ? renderTemplateList() : renderForm()}
            </Modal>

            <ConfirmModal
                visible={showConfirm}
                onConfirm={handleConfirmClose}
                onCancel={() => setShowConfirm(false)}
                title="Подтверждение закрытия"
                message="Данные не будут сохранены. Продолжить?"
                okText="Продолжить"
                cancelText="Отмена"
                type="warning"
            />
        </>
    );
};

export default CreateFromTemplateModal;
